import './styles.css';
import '@xterm/xterm/css/xterm.css';
import {
  createSession,
  listSessions,
  deleteSession,
  renameSession,
  listBackends,
  AuthError,
  BackendOfflineError,
  type BackendStatus,
  type SessionInfo,
} from './api';
import {
  createAgent,
  listAgents,
  deleteAgent,
  renameAgent,
  type AgentInfo,
} from './agent-api';
import { openTerminal } from './terminal-view';
import { openAgentView } from './agent-view';
import { renderLogin } from './login';
import { selectedTheme, applyChrome } from './themes';
import { openSettings } from './settings';
import { primeAudio } from './notify';

const app = document.getElementById('app')!;

// ---- App-shell state ------------------------------------------------------

// Mode is the top-level view: classic terminals or chat-first agents. Persisted
// so the app reopens in the view the user last used.
export type Mode = 'terminals' | 'agents';
const MODE_KEY = 'zw_mode';

export function currentMode(stored: string | null = localStorage.getItem(MODE_KEY)): Mode {
  return stored === 'agents' ? 'agents' : 'terminals';
}

export function setMode(m: Mode): void {
  localStorage.setItem(MODE_KEY, m);
}

// Sidebar collapsed state (wide screens only) is persisted so the layout the
// user prefers survives reloads. Default expanded. Pure — pass `stored` in tests.
const SIDEBAR_KEY = 'zw_sidebar';

export function sidebarCollapsed(stored: string | null = localStorage.getItem(SIDEBAR_KEY)): boolean {
  return stored === '1';
}

export function setSidebarCollapsed(collapsed: boolean): void {
  localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
}

export type LoadErrorKind = 'auth' | 'offline' | 'error';

// classifyLoadError maps a thrown load error to the screen we should show:
// AuthError → login, BackendOfflineError → offline screen, anything else →
// the generic error screen.
export function classifyLoadError(e: unknown): LoadErrorKind {
  if (e instanceof AuthError) return 'auth';
  if (e instanceof BackendOfflineError) return 'offline';
  return 'error';
}

// Module state. `dispose` tears down the open content view (terminal/agent);
// `backends` and `items` cache the last loads; `openId` is the selected item.
let dispose: (() => void) | null = null;
let backends: BackendStatus[] = [];
let items: (SessionInfo | AgentInfo)[] = [];
let openId: string | null = null;

function disposeContent(): void {
  if (dispose) {
    dispose();
    dispose = null;
  }
}

// isWide reports whether the persistent two-column layout is active (vs the
// phone off-canvas drawer). Mirrors the CSS breakpoint.
function isWide(): boolean {
  return window.matchMedia('(min-width: 820px)').matches;
}

// ---- Full-screen error / login states (replace the whole shell) -----------

function showLogin(): void {
  disposeContent();
  renderLogin(app, () => render());
}

function showOffline(): void {
  disposeContent();
  app.innerHTML = `
    <div class="notice">
      <h1>Can't reach your laptop</h1>
      <p>Your Mac isn't connected. Make sure the WireGuard tunnel is up (sudo wg-quick up zw0) and the server is running (zw-server -bind 10.7.0.2:4040).</p>
      <button id="retry">Retry</button>
    </div>`;
  document.getElementById('retry')!.onclick = () => render();
}

function showError(): void {
  disposeContent();
  app.innerHTML = `
    <div class="notice">
      <h1>Something went wrong</h1>
      <p>We couldn't load your sessions. Please try again.</p>
      <button id="retry">Retry</button>
    </div>`;
  document.getElementById('retry')!.onclick = () => render();
}

// handleLoadError routes a failed load to the right screen. Always handles the
// error (renders a screen); callers should return after calling it.
function handleLoadError(e: unknown): void {
  switch (classifyLoadError(e)) {
    case 'auth':
      showLogin();
      return;
    case 'offline':
      showOffline();
      return;
    default:
      showError();
  }
}

// ---- Shell ----------------------------------------------------------------

// render builds the whole shell (sidebar + content) then loads the list for the
// active mode. Used at bootstrap, on retry, and on mode switch.
async function render(): Promise<void> {
  disposeContent();
  openId = null;
  buildShell();
  await loadList(true);
}

function buildShell(): void {
  app.innerHTML = `
    <div class="shell" data-mode="${currentMode()}"${sidebarCollapsed() ? ' data-collapsed' : ''}>
      <aside class="sidebar">
        <div class="sidebar-head">
          <span class="sidebar-brand">ZionWave</span>
          <button class="collapse-btn" aria-label="Hide sidebar">«</button>
        </div>
        ${renderModeToggle(currentMode())}
        <button id="new" class="primary new-btn"></button>
        <ul id="list" class="sidebar-list"></ul>
        <div class="sidebar-foot">
          <button id="gear" class="gear-btn">⚙ Settings</button>
        </div>
      </aside>
      <div class="scrim" id="scrim"></div>
      <main class="content" id="content"></main>
    </div>`;
  wireShell();
  renderEmptyContent();
}

// wireShell hooks the persistent sidebar controls: mode toggle, "+ New",
// Settings gear, the collapse/close button, and the drawer scrim.
function wireShell(): void {
  app.querySelectorAll<HTMLButtonElement>('.seg').forEach(
    (b) =>
      (b.onclick = () => {
        const m = b.dataset.mode as Mode;
        if (m === currentMode()) return;
        setMode(m);
        void render();
      })
  );
  document.getElementById('gear')!.onclick = () =>
    openSettings(backends, { onBackendChange: () => void loadList() });
  document.querySelector<HTMLButtonElement>('.collapse-btn')!.onclick = () => {
    if (isWide()) setCollapsed(true);
    else closeDrawer();
  };
  document.getElementById('scrim')!.onclick = () => closeDrawer();
}

function setCollapsed(collapsed: boolean): void {
  setSidebarCollapsed(collapsed);
  const shell = document.querySelector('.shell');
  if (!shell) return;
  if (collapsed) shell.setAttribute('data-collapsed', '');
  else shell.removeAttribute('data-collapsed');
}

function openDrawer(): void {
  document.querySelector('.shell')?.setAttribute('data-drawer', 'open');
}

function closeDrawer(): void {
  document.querySelector('.shell')?.removeAttribute('data-drawer');
}

// onMenu is the content-area menu/hamburger: on wide it re-expands a collapsed
// sidebar; on phone it opens the off-canvas drawer.
function onMenu(): void {
  if (isWide()) setCollapsed(false);
  else openDrawer();
}

// loadList fetches backends + the active mode's list and renders the sidebar
// rows. On phone's first load with nothing open it opens the drawer so the user
// sees the list.
async function loadList(initial = false): Promise<void> {
  try {
    backends = await listBackends();
  } catch (e) {
    handleLoadError(e);
    return;
  }
  try {
    items = currentMode() === 'agents' ? await listAgents() : await listSessions();
  } catch (e) {
    handleLoadError(e);
    return;
  }
  wireNew();
  renderList();
  if (initial && !isWide() && !openId) openDrawer();
}

// renderModeToggle builds the Terminals | Chat segmented control. The active
// segment is marked; tapping the other switches mode and rebuilds the shell.
function renderModeToggle(active: Mode): string {
  const seg = (m: Mode, label: string) =>
    `<button class="seg${m === active ? ' seg-active' : ''}" data-mode="${m}" aria-pressed="${m === active}">${label}</button>`;
  return `<div class="mode-toggle" role="group" aria-label="View">${seg('terminals', 'Terminals')}${seg(
    'agents',
    'Chat'
  )}</div>`;
}

function wireNew(): void {
  const btn = document.getElementById('new') as HTMLButtonElement | null;
  if (!btn) return;
  btn.textContent = currentMode() === 'agents' ? '+ New chat' : '+ New terminal';
  btn.onclick = onNew;
}

async function onNew(): Promise<void> {
  if (!isWide()) closeDrawer();
  try {
    if (currentMode() === 'agents') {
      const cwd = prompt('Working directory for the agent:', '~');
      if (cwd === null) return;
      const id = await createAgent(cwd.trim() || undefined);
      await loadList();
      selectItem(id);
    } else {
      const id = await createSession(80, 24);
      await loadList();
      selectItem(id);
    }
  } catch (e) {
    if (e instanceof AuthError) showLogin();
    else throw e;
  }
}

// ---- Sidebar list ---------------------------------------------------------

// liveState reports whether a session is currently running, used to color the
// status dot. Server state strings vary, so we treat anything that looks
// exited/dead as not-live and everything else as live.
function liveState(state: string): boolean {
  const s = state.toLowerCase();
  return !(s.includes('exit') || s.includes('dead') || s.includes('stop') || s.includes('closed'));
}

// formatCreated renders a compact relative age ("just now", "5m", "2h", "3d").
export function formatCreated(createdAt: string, now: number = Date.now()): string {
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.floor((now - t) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// sessionName / agentName pick the display label: the effective name (server
// mirrors a rename into `title`), falling back to a sensible default.
function sessionName(s: SessionInfo): string {
  return s.name || s.title || 'terminal';
}

function agentName(a: AgentInfo): string {
  return a.name || a.title || a.cwd || 'agent';
}

function renderList(): void {
  const list = document.getElementById('list');
  if (!list) return;
  const mode = currentMode();
  if (items.length === 0) {
    list.innerHTML = `<li class="list-empty">${
      mode === 'agents' ? 'No chats yet. Tap “+ New chat”.' : 'No terminals yet. Tap “+ New terminal”.'
    }</li>`;
    return;
  }
  list.innerHTML = items
    .map((it) => (mode === 'agents' ? agentRow(it as AgentInfo) : sessionRow(it as SessionInfo)))
    .join('');
  wireRows();
}

function rowShell(id: string, dotClass: string, name: string): string {
  const active = id === openId ? ' row-active' : '';
  return `
    <li class="row${active}" data-id="${id}">
      <button class="row-open" data-id="${id}">
        <span class="dot ${dotClass}"></span>
        <span class="row-name" data-id="${id}">${escapeHtml(name)}</span>
      </button>
      <button class="row-edit" data-id="${id}" aria-label="Rename">✎</button>
      <button class="row-del" data-id="${id}" aria-label="Delete">✕</button>
    </li>`;
}

function sessionRow(s: SessionInfo): string {
  return rowShell(s.id, liveState(s.state) ? 'dot-live' : 'dot-idle', sessionName(s));
}

function agentRow(a: AgentInfo): string {
  return rowShell(a.id, a.busy ? 'dot-live' : 'dot-idle', agentName(a));
}

function wireRows(): void {
  const list = document.getElementById('list');
  if (!list) return;
  list.querySelectorAll<HTMLButtonElement>('.row-open').forEach((b) => (b.onclick = () => selectItem(b.dataset.id!)));
  list.querySelectorAll<HTMLButtonElement>('.row-edit').forEach(
    (b) =>
      (b.onclick = (e) => {
        e.stopPropagation();
        startRowEdit(b.dataset.id!);
      })
  );
  list.querySelectorAll<HTMLButtonElement>('.row-del').forEach(
    (b) =>
      (b.onclick = async (e) => {
        e.stopPropagation();
        await delItem(b.dataset.id!);
      })
  );
  list.querySelectorAll<HTMLElement>('.row-name').forEach((el) => (el.ondblclick = () => startRowEdit(el.dataset.id!)));
}

function markActive(): void {
  document.querySelectorAll<HTMLElement>('.row').forEach((row) => {
    row.classList.toggle('row-active', row.dataset.id === openId);
  });
}

async function delItem(id: string): Promise<void> {
  try {
    if (currentMode() === 'agents') await deleteAgent(id);
    else await deleteSession(id);
  } catch (e) {
    if (e instanceof AuthError) {
      showLogin();
      return;
    }
    throw e;
  }
  if (openId === id) {
    disposeContent();
    openId = null;
    renderEmptyContent();
  }
  await loadList();
}

// ---- Inline rename --------------------------------------------------------

// beginEdit swaps a name span for a text input. Enter/blur commits, Esc cancels.
// commit is invoked only when the trimmed value is non-empty and changed.
function beginEdit(span: HTMLElement, current: string, commit: (name: string) => Promise<void>): void {
  const input = document.createElement('input');
  input.className = 'name-input';
  input.value = current;
  input.setAttribute('aria-label', 'Name');
  span.replaceWith(input);
  input.focus();
  input.select();
  let settled = false;
  const finish = (save: boolean) => {
    if (settled) return;
    settled = true;
    const name = input.value.trim();
    input.replaceWith(span);
    if (save && name && name !== current) {
      span.textContent = name;
      void commit(name);
    }
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finish(true);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      finish(false);
    }
  });
  input.addEventListener('blur', () => finish(true));
}

function startRowEdit(id: string): void {
  const span = document.querySelector<HTMLElement>(`.row-name[data-id="${id}"]`);
  if (!span) return;
  beginEdit(span, span.textContent ?? '', (name) => commitRename(id, name));
}

function startHeaderEdit(id: string): void {
  const span = document.querySelector<HTMLElement>('#content .view-title');
  if (!span) return;
  beginEdit(span, span.textContent ?? '', (name) => commitRename(id, name));
}

// commitRename updates the cached item + both DOM copies (row and header)
// optimistically, then PATCHes. On failure it reloads from server truth.
async function commitRename(id: string, name: string): Promise<void> {
  const it = items.find((i) => i.id === id);
  if (it) {
    it.name = name;
    it.title = name;
  }
  syncName(id, name);
  try {
    if (currentMode() === 'agents') await renameAgent(id, name);
    else await renameSession(id, name);
  } catch (e) {
    if (e instanceof AuthError) {
      showLogin();
      return;
    }
    await loadList();
  }
}

// syncName keeps the row label and the content header label in step after a
// rename started from either place.
function syncName(id: string, name: string): void {
  const row = document.querySelector<HTMLElement>(`.row-name[data-id="${id}"]`);
  if (row) row.textContent = name;
  if (openId === id) {
    const header = document.querySelector<HTMLElement>('#content .view-title');
    if (header) header.textContent = name;
  }
}

// ---- Content area ---------------------------------------------------------

function selectItem(id: string): void {
  openId = id;
  if (!isWide()) closeDrawer();
  markActive();
  openContent(id);
}

function openContent(id: string): void {
  disposeContent();
  if (currentMode() === 'agents') openAgentContent(id);
  else openTerminalContent(id);
}

// attachHint renders a dim one-liner telling the user how to attach to the SAME
// live terminal from their Mac via tmux. Returns '' when not tmux-backed.
export function attachHint(tmux?: string): string {
  if (!tmux) return '';
  return `<div class="attach-hint">On your Mac: <code>tmux attach -t ${tmux}</code></div>`;
}

function contentHeader(id: string, name: string): string {
  return `
    <header class="content-header term-header">
      <button class="menu-btn" aria-label="Menu">☰</button>
      <span class="title-wrap">
        <span class="view-title" data-id="${id}">${escapeHtml(name)}</span>
        <button class="title-edit" aria-label="Rename">✎</button>
      </span>
      <span id="conn-status" class="conn-status" data-status="connecting">Connecting…</span>
    </header>`;
}

function wireContentHeader(id: string): void {
  const content = document.getElementById('content')!;
  content.querySelector<HTMLButtonElement>('.menu-btn')!.onclick = onMenu;
  const editBtn = content.querySelector<HTMLButtonElement>('.title-edit');
  const titleEl = content.querySelector<HTMLElement>('.view-title');
  if (editBtn) editBtn.onclick = () => startHeaderEdit(id);
  if (titleEl) titleEl.ondblclick = () => startHeaderEdit(id);
}

function openTerminalContent(id: string): void {
  const s = items.find((i) => i.id === id) as SessionInfo | undefined;
  const content = document.getElementById('content')!;
  content.innerHTML = `
    <div class="view term-screen">
      ${contentHeader(id, s ? sessionName(s) : 'terminal')}
      ${attachHint(s?.tmux)}
      <div id="term"></div>
    </div>`;
  wireContentHeader(id);
  dispose = openTerminal(document.getElementById('term')!, id);
}

function openAgentContent(id: string): void {
  const a = items.find((i) => i.id === id) as AgentInfo | undefined;
  const content = document.getElementById('content')!;
  content.innerHTML = `
    <div class="view agent-screen">
      ${contentHeader(id, a ? agentName(a) : 'agent')}
      <div id="agent-body"></div>
    </div>`;
  wireContentHeader(id);
  dispose = openAgentView(document.getElementById('agent-body')!, id);
}

// renderEmptyContent shows the placeholder when no item is selected, with a
// menu affordance (phone / collapsed) and a "+ New" shortcut.
function renderEmptyContent(): void {
  const content = document.getElementById('content');
  if (!content) return;
  const mode = currentMode();
  content.innerHTML = `
    <div class="view content-empty">
      <header class="content-header">
        <button class="menu-btn" aria-label="Menu">☰</button>
        <span class="title-wrap"></span>
      </header>
      <div class="empty-mid">
        <p>${mode === 'agents' ? 'Pick a chat, or start a new one.' : 'Pick a terminal, or start a new one.'}</p>
        <button id="empty-new" class="primary">${mode === 'agents' ? '+ New chat' : '+ New terminal'}</button>
      </div>
    </div>`;
  content.querySelector<HTMLButtonElement>('.menu-btn')!.onclick = onMenu;
  content.querySelector<HTMLButtonElement>('#empty-new')!.onclick = onNew;
}

// escapeHtml guards interpolated, user-influenced strings (names, cwd) in the
// list/header HTML against markup injection.
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
  );
}

// Bootstrap only in a real document (skipped under unit tests, which import the
// pure helpers without a #app host).
if (document.getElementById('app')) {
  applyChrome(selectedTheme());
  // Prime WebAudio on the first user gesture so later turn-done chimes can sound.
  window.addEventListener('pointerdown', primeAudio, { once: true });
  void render();
}
